import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOperatorHistory } from '@/hooks/useOperatorHistory';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { canViewTraceabilityHistory } from '@/lib/roleUtils';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Clock,
  Package,
  Users,
  Pause,
  Download,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

export default function TraceabilityHistory() {
  const { userRole } = useAuth();
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<string>('all');

  // Check permissions
  if (!canViewTraceabilityHistory(userRole)) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Acceso Restringido</h2>
          <p className="text-muted-foreground">
            No tienes permisos para ver el historial de trazabilidad.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Load operators
  const { data: operators } = useQuery({
    queryKey: ['operators-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name');
      if (error) throw error;
      return data;
    },
  });

  // Load work orders
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-list'],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  const filters = {
    operatorId: selectedOperator !== 'all' ? selectedOperator : undefined,
    workOrderId: selectedOrder !== 'all' ? selectedOrder : undefined,
    dateFrom,
    dateTo,
  };

  const { productionLogs, pauses, summary, isLoading, refetch } = useOperatorHistory(filters);

  const exportToCSV = () => {
    const headers = ['Fecha', 'Operario', 'OT', 'Producto', 'Piezas', 'Turno'];
    const rows = productionLogs.map(log => [
      format(new Date(log.created_at), 'dd/MM/yyyy HH:mm'),
      log.operator?.full_name || '-',
      log.work_order?.order_number || '-',
      log.work_order?.product_name || '-',
      log.pieces_completed,
      log.shift || '-',
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trazabilidad_${dateFrom}_${dateTo}.csv`;
    link.click();
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Historial de Trazabilidad</h1>
            <p className="text-muted-foreground">
              Registro de producción y pausas por operario
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Desde</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Hasta</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Operario</label>
                <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los operarios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {operators?.map((op) => (
                      <SelectItem key={op.id} value={op.id}>
                        {op.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Orden de Trabajo</label>
                <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las OTs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {workOrders?.map((wo) => (
                      <SelectItem key={wo.id} value={wo.id}>
                        {wo.order_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{summary.totalPieces}</p>
                    <p className="text-sm text-muted-foreground">Piezas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{summary.uniqueOrders}</p>
                    <p className="text-sm text-muted-foreground">OTs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{summary.uniqueOperators}</p>
                    <p className="text-sm text-muted-foreground">Operarios</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Pause className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="text-2xl font-bold">{summary.totalPauses}</p>
                    <p className="text-sm text-muted-foreground">Pausas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold">{Math.round(summary.totalPauseMinutes / 60)}h</p>
                    <p className="text-sm text-muted-foreground">Tiempo pausado</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tabs */}
        <Tabs defaultValue="production">
          <TabsList>
            <TabsTrigger value="production">
              Producción ({productionLogs.length})
            </TabsTrigger>
            <TabsTrigger value="pauses">
              Pausas ({pauses.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="production">
            <Card>
              <CardContent className="pt-6">
                {isLoading ? (
                  <div className="text-center py-8">Cargando...</div>
                ) : productionLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay registros de producción en este período
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha/Hora</TableHead>
                        <TableHead>Operario</TableHead>
                        <TableHead>OT</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Piezas</TableHead>
                        <TableHead>Turno</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productionLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </TableCell>
                          <TableCell>{log.operator?.full_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.work_order?.order_number || '-'}</Badge>
                          </TableCell>
                          <TableCell>{log.work_order?.product_name || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {log.pieces_completed}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{log.shift || '-'}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pauses">
            <Card>
              <CardContent className="pt-6">
                {isLoading ? (
                  <div className="text-center py-8">Cargando...</div>
                ) : pauses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay pausas registradas en este período
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Inicio</TableHead>
                        <TableHead>Fin</TableHead>
                        <TableHead>Duración</TableHead>
                        <TableHead>Operario</TableHead>
                        <TableHead>OT</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pauses.map((pause) => (
                        <TableRow key={pause.id}>
                          <TableCell>
                            {format(new Date(pause.started_at), 'dd/MM HH:mm', { locale: es })}
                          </TableCell>
                          <TableCell>
                            {pause.ended_at
                              ? format(new Date(pause.ended_at), 'dd/MM HH:mm', { locale: es })
                              : <Badge variant="destructive">En curso</Badge>
                            }
                          </TableCell>
                          <TableCell>
                            {pause.duration_minutes
                              ? `${pause.duration_minutes} min`
                              : '-'
                            }
                          </TableCell>
                          <TableCell>{pause.operator?.full_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{pause.work_order?.order_number || '-'}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {pause.reason || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
